import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight, Play, AlertCircle, LucideIcon } from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { SubscriptionTier } from "@/dentlab/src/store/Central/types";
import { RolesEnum } from "@/lib/types/roles.types";
import { CALENDLY_DEMO_URL } from "@/lib/constants/urls";

interface BannerContentProps {
	expanded: boolean;
	bgColor: string;
	titleColor: string;
	textColor: string;
	iconColor: string;
	message: string;
	buttonText: string;
	ariaLabel: string;
	dataTestId: string;
	title: string;
	Icon: LucideIcon;
	onClick: () => void;
}

const BannerContent: React.FC<BannerContentProps> = ({
	expanded,
	bgColor,
	titleColor,
	textColor,
	iconColor,
	message,
	buttonText,
	ariaLabel,
	dataTestId,
	title,
	Icon,
	onClick,
}) => (
	<div
		className={cn(
			"flex items-center gap-2 py-2 pl-2 pr-3 rounded-md border cursor-pointer transition-colors",
			bgColor
		)}
		onClick={onClick}
		title={title}
		aria-label={ariaLabel}
		data-testid={dataTestId}
	>
		<div className="w-6 h-6 flex items-center justify-center">
			<Icon className={cn("h-4 w-4", iconColor)} />
		</div>
		{expanded && (
			<div className="flex items-center gap-2">
				<div className="flex-1">
					<div className={cn("text-xs font-medium", titleColor)}>
						{message}
					</div>
					{buttonText && (
						<div
							className={cn(
								"text-xs flex items-center gap-1",
								textColor
							)}
						>
							{buttonText}
							<ArrowRight className="h-3 w-3" />
						</div>
					)}
				</div>
			</div>
		)}
	</div>
);

interface SubscriptionBannerProps {
	expanded: boolean;
}

export const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({
	expanded,
}) => {
	const { role, subscription, subscriptionTier } = useCentralStore(
		(state) => ({
			role: state.role ?? RolesEnum.MEMBER,
			subscription: state.subscription,
			subscriptionTier: state.subscriptionTier,
		})
	);
	const trialDaysRemaining = useCentralStore((state) =>
		state.getTrialDaysRemaining()
	);
	const navigate = useNavigate();

	const shouldShowBanner = (
		tier: SubscriptionTier | null
	): tier is SubscriptionTier => {
		if (!tier || tier === "full") return false;
		if (role !== RolesEnum.OWNER) return false;
		return true;
	};

	const handleClick = () => {
		if (subscriptionTier === "eligible") {
			window.open(CALENDLY_DEMO_URL, "_blank");
		} else {
			navigate("/organisation/administration/abonnement");
		}
	};

	if (!shouldShowBanner(subscriptionTier)) {
		return null;
	}

	switch (subscriptionTier) {
		case "eligible":
			return (
				<BannerContent
					expanded={expanded}
					bgColor="bg-green-50 border-green-200 hover:bg-green-100"
					iconColor="text-green-600"
					titleColor="text-green-900"
					textColor="text-green-700"
					message="Testversion verfügbar"
					buttonText="Kostenlose Demo"
					ariaLabel="start-trial-banner"
					dataTestId="start-trial-banner"
					title="Demo buchen"
					Icon={Play}
					onClick={handleClick}
				/>
			);

		case "expired":
			return (
				<BannerContent
					expanded={expanded}
					bgColor="bg-red-50 border-red-200 hover:bg-red-100"
					iconColor="text-red-600"
					titleColor="text-red-900"
					textColor="text-red-700"
					message="Testversion abgelaufen"
					buttonText="Jetzt upgraden"
					ariaLabel="expired-banner"
					dataTestId="expired-banner"
					title="Zum Abonnement"
					Icon={AlertCircle}
					onClick={handleClick}
				/>
			);

		case "trial":
			const hasPendingSubscription =
				subscription?.subscription_id != null;
			return hasPendingSubscription ? (
				<BannerContent
					expanded={expanded}
					bgColor="bg-blue-50 border-blue-200 hover:bg-blue-100"
					iconColor="text-blue-600"
					titleColor="text-blue-900"
					textColor="text-blue-700"
					message={`Abo startet in ${trialDaysRemaining} Tagen`}
					buttonText=""
					ariaLabel="subscription-banner"
					dataTestId="subscription-banner"
					title="Zum Abonnement"
					Icon={Clock}
					onClick={handleClick}
				/>
			) : (
				<BannerContent
					expanded={expanded}
					bgColor="bg-blue-50 border-blue-200 hover:bg-blue-100"
					iconColor="text-blue-600"
					titleColor="text-blue-900"
					textColor="text-blue-700"
					message={`Testversion: ${trialDaysRemaining} Tage`}
					buttonText="Jetzt upgraden"
					ariaLabel="subscription-banner"
					dataTestId="subscription-banner"
					title="Zum Abonnement"
					Icon={Clock}
					onClick={handleClick}
				/>
			);

		case "full":
			return null;
	}
};
